import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import './AdminSettingsPage.css';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminSettingsPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newLink, setNewLink] = useState({ label: '', path: '', type: 'link' });
    const [expandedLinkId, setExpandedLinkId] = useState(null);

    const languages = ['it', 'en', 'de', 'nl', 'fr'];

    // Percorsi delle pagine hardcoded nell'app (non modificabili tramite il builder)
    const HARDCODED_PATHS = [
        '/', '/#', '/camere', '/servizi', '/esperienze', '/galleria',
        '/posizione', '/contatti', '/privacy-policy', '/termini-e-condizioni',
        '/configura-camera', '/admin-login-segreto-xyz123', '/admin-impostazioni-sito'
    ];

    const isDynamicPage = (link) => {
        return link.type === 'link' && !HARDCODED_PATHS.includes(link.path);
    };

    useEffect(() => {
        const loadSettings = async () => {
            if (!currentUser) {
                navigate('/admin-login-segreto-xyz123');
                return;
            }
            setLoading(true);
            setError('');
            try {
                const settingsDocRef = doc(db, 'site_settings', 'global');
                const docSnap = await getDoc(settingsDocRef);
                if (docSnap.exists()) {
                    const dbData = docSnap.data();
                    // Assicura che i nuovi campi esistano per i link vecchi
                    const sanitizedLinks = (dbData.navbarLinks || []).map(link => ({
                        ...link,
                        showInHeader: link.showInHeader !== false, // Default a true se non definito
                        showInFooter: link.showInFooter !== false, // Default a true se non definito
                    }));
                    const sortedLinks = sanitizedLinks.sort((a, b) => a.order - b.order);
                    setSettings({ ...dbData, navbarLinks: sortedLinks });
                } else {
                    console.log("Documento impostazioni non trovato. Verrà creato al primo salvataggio.");
                    setSettings({ siteName: "L'antico Piol De Feltre", siteLogoUrl: "/logo192.png", navbarLinks: [] });
                }
            } catch (err) {
                console.error("Errore caricamento impostazioni:", err);
                setError("Errore nel caricamento delle impostazioni.");
            } finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, [currentUser, navigate]);

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
        setSuccess(''); setError('');
    };

    const handleLinkChange = (index, field, value) => {
        const updatedLinks = [...settings.navbarLinks];
        updatedLinks[index] = { ...updatedLinks[index], [field]: value };
        setSettings(prev => ({ ...prev, navbarLinks: updatedLinks }));
        setSuccess(''); setError('');
    };

    const handleLinkTranslationChange = (index, lang, value) => {
        const updatedLinks = [...settings.navbarLinks];
        updatedLinks[index] = {
            ...updatedLinks[index],
            translations: {
                ...(updatedLinks[index].translations || {}),
                [lang]: value
            }
        };
        setSettings(prev => ({ ...prev, navbarLinks: updatedLinks }));
        setSuccess(''); setError('');
    };

    const handleAddNewLink = async () => {
        if (!newLink.label || !newLink.path) {
            alert("Etichetta e Percorso sono obbligatori.");
            return;
        }

        let cleanPath = newLink.path;
        if (newLink.type !== 'external_link') {
            cleanPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
        }

        const newLinkObject = {
            id: newLink.label.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, ''),
            label: newLink.label,
            path: cleanPath,
            order: (settings.navbarLinks.length + 1) * 10,
            enabled: true,
            type: newLink.type,
            showInHeader: true, // Default
            showInFooter: true, // Default
        };

        if (newLink.type === 'link') {
            try {
                // Remove leading slash for document ID
                const docId = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath;
                const dynamicPageRef = doc(db, 'dynamic_pages', docId);
                await setDoc(dynamicPageRef, {
                    path: cleanPath,
                    title: newLink.label,
                    blocks: [],
                    createdAt: serverTimestamp()
                }, { merge: true });
            } catch (err) {
                console.error("Errore creazione pagina dinamica:", err);
                alert("Errore durante la creazione della pagina dinamica.");
                return;
            }
        }

        const updatedLinks = [...settings.navbarLinks, newLinkObject];

        // Salva immediatamente su Firebase per non perdere il link se si naviga via
        try {
            setSaving(true);
            const settingsDocRef = doc(db, 'site_settings', 'global');
            const updatedSettings = { ...settings, navbarLinks: updatedLinks };
            await setDoc(settingsDocRef, { ...updatedSettings, lastUpdatedBy: currentUser.email, updatedAt: serverTimestamp() }, { merge: true });
            setSettings(updatedSettings);
            setSuccess('✅ Link aggiunto e salvato!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("Errore salvataggio link:", err);
            setError("Link creato ma non salvato. Premi 'Salva Impostazioni'.");
            setSettings(prev => ({ ...prev, navbarLinks: updatedLinks }));
        } finally {
            setSaving(false);
        }

        setNewLink({ label: '', path: '', type: 'link' });
    };

    const handleDeleteLink = (indexToDelete, label) => {
        const linkToDelete = settings.navbarLinks[indexToDelete];
        if (['home', 'camere', 'servizi'].includes(linkToDelete.id)) {
            alert(`La sezione "${label}" è fondamentale e non può essere eliminata.`);
            return;
        }
        if (window.confirm(`Sei sicuro di voler eliminare il link "${label}"?`)) {
            const updatedLinks = settings.navbarLinks.filter((_, index) => index !== indexToDelete);
            setSettings(prev => ({ ...prev, navbarLinks: updatedLinks }));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(''); setSuccess('');
        try {
            const settingsDocRef = doc(db, 'site_settings', 'global');
            await setDoc(settingsDocRef, { ...settings, lastUpdatedBy: currentUser.email, updatedAt: serverTimestamp() }, { merge: true });
            setSuccess('Impostazioni salvate con successo!');
            // Ricarica per riordinare
            const docSnap = await getDoc(settingsDocRef);
            if(docSnap.exists()) {
                const dbData = docSnap.data();
                const sanitizedLinks = (dbData.navbarLinks || []).map(link => ({...link, showInHeader: link.showInHeader !== false, showInFooter: link.showInFooter !== false }));
                const sortedLinks = sanitizedLinks.sort((a, b) => a.order - b.order);
                setSettings({ ...dbData, navbarLinks: sortedLinks });
            }
        } catch (err) {
            console.error("Errore salvataggio impostazioni:", err);
            setError("Errore durante il salvataggio.");
        } finally {
            setSaving(false);
            if (!error) { setTimeout(() => setSuccess(''), 4000); }
        }
    };

    if (loading) {
        return <LoadingSpinner show={true} />;
    }

    if (error && !saving) {
        return (
            <div className="admin-settings-page">
                 <section className="page-header">
                    <div className="container"><h1>Errore</h1></div>
                </section>
                <div className="container form-status error">{error}</div>
            </div>
        );
    }

    if (!settings) {
        return (
             <div className="admin-settings-page">
                 <section className="page-header">
                    <div className="container"><h1>Errore</h1></div>
                </section>
                <div className="container no-content-text">Impossibile inizializzare le impostazioni.</div>
            </div>
        );
    }

    return (
        <div className="admin-settings-page">
            <section className="page-header">
                <div className="container">
                    <h1>Impostazioni Sito</h1>
                    <p>Modifica le configurazioni globali.</p>
                </div>
            </section>
            <section className="content-section">
                <div className="container settings-form-container">
                    <form onSubmit={handleSave}>
                        <h2>Generali</h2>
                        <div className="form-group">
                            <label htmlFor="siteName">Nome Sito (Header)</label>
                            <input type="text" id="siteName" name="siteName" value={settings.siteName || ''} onChange={handleSettingsChange} disabled={saving}/>
                        </div>
                        <div className="form-group">
                            <label htmlFor="siteLogoUrl">URL Logo/Favicon</label>
                            <input type="text" id="siteLogoUrl" name="siteLogoUrl" value={settings.siteLogoUrl || ''} onChange={handleSettingsChange} disabled={saving} placeholder="/logo192.png o https://..."/>
                        </div>
                        <hr className="form-divider" />
                        
                        <h2>Link di Navigazione</h2>
                        <p>Modifica etichetta, ordine e visibilità dei link. L'ordine è crescente (10 prima di 20).</p>
                        <div className="navbar-links-editor">
                            {(settings.navbarLinks || []).map((link, index) => (
                                <div key={link.id || index} className="link-editor-item-container">
                                    <div className="link-editor-item">
                                        <input type="number" className="link-order" value={link.order} onChange={(e) => handleLinkChange(index, 'order', parseInt(e.target.value, 10) || 0)} title="Ordine"/>
                                        <input type="text" className="link-label" value={link.label} onChange={(e) => handleLinkChange(index, 'label', e.target.value)} title="Etichetta"/>
                                        <span className="link-path" title="Percorso (non modificabile)">{link.path}</span>
                                        
                                        <button type="button" className="edit-link-btn" onClick={() => setExpandedLinkId(expandedLinkId === index ? null : index)} title="Modifica Etichette e Visibilità">✏️ EDIT</button>
                                        {isDynamicPage(link) && (
                                            <button type="button" className="edit-content-btn" onClick={() => navigate(`/admin-page-builder${link.path}`)} title="Modifica Contenuto Pagina">🎨 BUILD</button>
                                        )}
                                        <button type="button" className="delete-link-btn" onClick={() => handleDeleteLink(index, link.label)} disabled={['home', 'camere', 'servizi'].includes(link.id)} title={['home', 'camere', 'servizi'].includes(link.id) ? "Sezione non eliminabile" : "Elimina Link"}>🗑️</button>
                                    </div>
                                    
                                    {expandedLinkId === index && (
                                        <div className="link-editor-expanded">
                                            <div className="link-visibility-controls">
                                                <label title="Mostra nell'header"><input type="checkbox" checked={link.showInHeader} onChange={(e) => handleLinkChange(index, 'showInHeader', e.target.checked)}/> Header</label>
                                                <label title="Mostra nel footer"><input type="checkbox" checked={link.showInFooter} onChange={(e) => handleLinkChange(index, 'showInFooter', e.target.checked)}/> Footer</label>
                                            </div>
                                            <div className="link-translations">
                                                <h4>Traduzioni Etichetta</h4>
                                                {languages.map(lang => (
                                                    <div key={lang} className="translation-input-group">
                                                        <label>{lang.toUpperCase()}</label>
                                                        <input type="text" value={link.translations?.[lang] || ''} onChange={(e) => handleLinkTranslationChange(index, lang, e.target.value)} placeholder={`Traduzione ${lang.toUpperCase()}`}/>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <div className="add-link-form">
                             <h4>Aggiungi Nuovo Link</h4>
                             <div className="form-inline">
                                 <div className="form-group">
                                     <label>Etichetta</label>
                                     <input type="text" value={newLink.label} onChange={(e) => setNewLink(p => ({...p, label: e.target.value}))} placeholder="Nuova Pagina"/>
                                 </div>
                                 <div className="form-group">
                                     <label>Percorso</label>
                                     <input type="text" value={newLink.path} onChange={(e) => setNewLink(p => ({...p, path: e.target.value}))} placeholder="/nuova-pagina"/>
                                 </div>
                                 <div className="form-group">
                                     <label>Tipo</label>
                                     <select value={newLink.type} onChange={(e) => setNewLink(p => ({...p, type: e.target.value}))}>
                                         <option value="link">Link Interno</option>
                                         <option value="external_link">Link Esterno</option>
                                     </select>
                                 </div>
                                 <button type="button" onClick={handleAddNewLink} className="add-link-btn">+</button>
                             </div>
                        </div>

                        <hr className="form-divider" />
                        <h2>Effetti Stagionali</h2>
                        <p>Scegli l'atmosfera visiva del sito. Il colore principale e gli effetti decorativi cambieranno automaticamente.</p>
                        <div className="form-group">
                            <label htmlFor="season">Stagione attiva</label>
                            <select
                                id="season"
                                name="season"
                                value={settings.season || 'none'}
                                onChange={handleSettingsChange}
                                disabled={saving}
                                style={{ padding: '10px 14px', borderRadius: 'var(--border-radius)', border: '1px solid var(--medium-gray)', fontSize: '1rem', width: '100%' }}
                            >
                                <option value="none">🌿 Nessuno (default)</option>
                                <option value="spring">🌸 Primavera</option>
                                <option value="summer">🌻 Estate</option>
                                <option value="autumn">🍂 Autunno</option>
                                <option value="winter">❄️ Inverno</option>
                            </select>
                        </div>

                        <hr className="form-divider" />
                        {error && <p className="form-status error">{error}</p>}
                        {success && <p className="form-status success">{success}</p>}
                    </form>
                </div>
                <div className="container centered-form-actions">
                     <button type="button" className="cta-button save-settings-button" disabled={saving} onClick={handleSave}>
                        {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
                    </button>
                </div>
            </section>
        </div>
    );
};

export default AdminSettingsPage;